import React, { useState, useMemo } from "react";
import { Table, Button, Input, Space, Typography, Tag, Drawer, Tooltip, Select, message } from "antd";
import { Search, Upload, Eye } from "lucide-react";

const { Title, Text } = Typography;
const { Option } = Select;

const TransactionIndex = () => {
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const openTransactionDrawer = (record) => {
        setSelectedTransaction(record);
        setIsDrawerOpen(true);
    };

    const closeTransactionDrawer = () => {
        setIsDrawerOpen(false);
    };

    const dataSource = [
        {
            key: '1',
            id: '37',
            transactionId: 'TXN1001',
            amount: 120,
            invoiceId: 'INV5001',
            orderId: '69',
            paymentMethod: 'Cash On Delivery',
            status: 'Refunded',
            date: '2024-10-21 04:50:23',
        },
        {
            key: '2',
            id: '36',
            transactionId: 'TXN1002',
            amount: 95,
            invoiceId: 'INV5002',
            orderId: '68',
            paymentMethod: 'Cash On Delivery',
            status: 'Refunded',
            date: '2024-10-14 10:58:55',
        },
        {
            key: '3',
            id: '35',
            transactionId: 'TXN1003',
            amount: 80,
            invoiceId: 'INV5003',
            orderId: '67',
            paymentMethod: 'Online Payment',
            status: 'Pending',
            date: '2024-10-10 07:14:38',
        },
        {
            key: '4',
            id: '34',
            transactionId: 'TXN1004',
            amount: 150,
            invoiceId: 'INV5004',
            orderId: '64',
            paymentMethod: 'Cash On Delivery',
            status: 'Refunded',
            date: '2024-10-10 06:50:29',
        },
        {
            key: '5',
            id: '33',
            transactionId: 'TXN1005',
            amount: 60,
            invoiceId: 'INV5005',
            orderId: '66',
            paymentMethod: 'Online Payment',
            status: 'Failed',
            date: '2024-10-10 06:45:55',
        },
        {
            key: '6',
            id: '32',
            transactionId: 'TXN1006',
            amount: 45,
            invoiceId: 'INV5006',
            orderId: '62',
            paymentMethod: 'Cash On Delivery',
            status: 'Refunded',
            date: '2024-10-07 07:06:03',
        },
        {
            key: '7',
            id: '31',
            transactionId: 'TXN1007',
            amount: 200,
            invoiceId: 'INV5007',
            orderId: '43',
            paymentMethod: 'Cash On Delivery',
            status: 'Refunded',
            date: '2024-10-07 06:46:01',
        },
    ];

    // Filter data based on search text and status filter
    const filteredData = useMemo(() => {
        return dataSource.filter((item) => {
            const query = searchText.trim().toLowerCase();
            const matchesSearch = !query || (
                (item.transactionId && item.transactionId.toLowerCase().includes(query)) ||
                (item.invoiceId && item.invoiceId.toLowerCase().includes(query)) ||
                (item.orderId && item.orderId.toLowerCase().includes(query)) ||
                (item.amount !== undefined && item.amount.toString().includes(query)) ||
                (item.paymentMethod && item.paymentMethod.toLowerCase().includes(query)) ||
                (item.status && item.status.toLowerCase().includes(query)) ||
                (item.date && item.date.toLowerCase().includes(query))
            );

            const matchesStatus = statusFilter === "all" || (
                item.status && item.status.toLowerCase() === statusFilter.toLowerCase()
            );

            return matchesSearch && matchesStatus;
        }).map((item, index) => ({
            ...item,
            srNo: index + 1,
        }));
    }, [searchText, statusFilter]);

    const getStatusTag = (status) => {
        let color;
        switch (status?.toLowerCase()) {
            case 'refunded':
                color = 'success';
                break;
            case 'pending':
                color = 'warning';
                break;
            case 'failed':
                color = 'error';
                break;
            case 'processing':
                color = 'processing';
                break;
            default:
                color = 'default';
        }
        return <Tag color={color}>{status}</Tag>;
    };

    const handleExport = () => {
        if (filteredData.length === 0) {
            message.warning("No transactions to export");
            return;
        }

        const headers = ["SR No.", "Transaction ID", "Amount", "Invoice ID", "Order ID", "Payment Method", "Status", "Date"];
        const csvRows = [headers.join(",")];

        filteredData.forEach((item, index) => {
            const row = [
                index + 1,
                `"${item.transactionId || ''}"`,
                item.amount,
                `"${item.invoiceId || ''}"`,
                `"${item.orderId || ''}"`,
                `"${item.paymentMethod || ''}"`,
                `"${item.status || ''}"`,
                `"${item.date || ''}"`,
            ];
            csvRows.push(row.join(","));
        });

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const timestamp = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `transactions_${timestamp}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        message.success(`${filteredData.length} transaction(s) exported successfully`);
    };

    const columns = [
        {
            title: 'SR No.',
            dataIndex: 'srNo',
            key: 'srNo',
            render: (text) => <Text style={{ color: '#64748b' }}>{text}</Text>,
        },
        {
            title: 'Transaction ID',
            dataIndex: 'transactionId',
            key: 'transactionId',
            render: (text) => <Text style={{ color: '#64748b' }}>{text}</Text>,
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (text) => <Text style={{ color: '#64748b' }}>{text}</Text>,
        },
        {
            title: 'Invoice ID',
            dataIndex: 'invoiceId',
            key: 'invoiceId',
            render: (text) => <Text style={{ color: '#64748b' }}>{text}</Text>,
        },
        {
            title: 'Order ID',
            dataIndex: 'orderId',
            key: 'orderId',
            render: (text) => <Text style={{ color: '#64748b' }}>{text}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text) => getStatusTag(text),
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => <Text style={{ color: '#64748b' }}>{text}</Text>,
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Tooltip title="View Details">
                    <Button
                        type="text"
                        icon={<Eye size={18} style={{ color: "#2563eb" }} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            openTransactionDrawer(record);
                        }}
                    />
                </Tooltip>
            ),
        },
    ];

    const transactionDetails = selectedTransaction
        ? [
            { label: 'Transaction ID', value: selectedTransaction.transactionId },
            { label: 'Order ID', value: selectedTransaction.orderId },
            { label: 'Invoice ID', value: selectedTransaction.invoiceId },
            { label: 'Payment Method', value: selectedTransaction.paymentMethod },
            { label: 'Status', value: getStatusTag(selectedTransaction.status) },
            { label: 'Created At', value: selectedTransaction.date },
            { label: 'Amount', value: `${selectedTransaction.amount}` },
        ]
        : [];

    return (
        <div className="shipments-container" style={{ padding: '24px', background: '#fff', minHeight: '100vh', borderRadius: "10px" }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <Title level={3} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>Transactions</Title>
                <Button
                    icon={<Upload size={16} />}
                    onClick={handleExport}
                    disabled={filteredData.length === 0}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '6px',
                        color: '#64748b',
                        fontWeight: 500,
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none'
                    }}
                >
                    Export {filteredData.length > 0 && `(${filteredData.length})`}
                </Button>
            </div>

            {/* Filters Bar: Search + Status Filter */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <Space size="middle" wrap>
                    <Input
                        placeholder="Search by ID, invoice, order, amount..."
                        prefix={<Search size={16} color="#94a3b8" />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        style={{ width: 280, borderRadius: '6px' }}
                    />
                    <Select
                        value={statusFilter}
                        onChange={(val) => setStatusFilter(val)}
                        style={{ width: 160 }}
                        placeholder="Filter by Status"
                    >
                        <Option value="all">All Status</Option>
                        <Option value="Refunded">Refunded</Option>
                        <Option value="Pending">Pending</Option>
                        <Option value="Failed">Failed</Option>
                    </Select>
                    {(searchText || statusFilter !== "all") && (
                        <Button
                            type="link"
                            onClick={() => {
                                setSearchText("");
                                setStatusFilter("all");
                            }}
                            style={{ padding: 0, color: '#2563eb' }}
                        >
                            Reset Filters
                        </Button>
                    )}
                </Space>
                <div>
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                        Showing {filteredData.length} of {dataSource.length} transactions
                    </Text>
                </div>
            </div>

            {/* Transactions Table */}
            <Table
                scroll={{ x: 'max-content' }}
                columns={columns}
                dataSource={filteredData}
                rowKey="key"
                pagination={false}
                style={{ border: '1px solid #f1f5f9', borderRadius: '8px' }}
                className="reward-table-custom"
                locale={{ emptyText: "No transactions found matching your criteria" }}
            />

            {/* Details Drawer */}
            <Drawer
                title="Transaction Details"
                placement="right"
                width={420}
                open={isDrawerOpen}
                onClose={closeTransactionDrawer}
                bodyStyle={{ padding: '20px 24px' }}
            >
                <Title level={5} style={{ marginTop: 0, marginBottom: 24, color: '#334155', fontWeight: 600 }}>
                    Transaction Data
                </Title>

                <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 12, columnGap: 8, alignItems: 'center' }}>
                    {transactionDetails.map((detail) => (
                        <React.Fragment key={detail.label}>
                            <Text style={{ color: '#64748b', fontSize: 14 }}>{detail.label}</Text>
                            <div style={{ color: '#0f172a', fontSize: 14, fontWeight: 500 }}>{detail.value}</div>
                        </React.Fragment>
                    ))}
                </div>
            </Drawer>
        </div>
    );
};

export default TransactionIndex;
